import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import './index.css';
import profilePic from './files/avatar.jpg';
import cvLink from './files/cv.pdf';
import { Mail, FileText, Github, Linkedin, Download, Star } from 'lucide-react';
import PublicationList from './components/Publications.tsx';
import examplePublications from './pubs.json';
import ResearchPage from './components/ResearchPage';
import Talks from './components/Talks.tsx';
import Blog from './components/Blog.tsx';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Tabs = () => {
  const location = useLocation();
  const tabs = [
    { name: 'Home', path: '/' },
    { name: 'Publications', path: '/publications' },
    { name: 'Blog', path: '/blog' },
    { name: 'Research', path: '/research' },
    { name: 'Talks', path: '/talks' },
  ];

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-zinc-200/70">
      <nav className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center">
        <div className="flex gap-0.5 sm:gap-1 overflow-x-auto scrollbar-hide -mx-2 px-2">
          {tabs.map((tab) => (
            <Link
              key={tab.name}
              to={tab.path}
              className={`
                px-2 sm:px-3 py-1.5 rounded-md text-[0.85rem] sm:text-[0.9rem] font-medium whitespace-nowrap flex-shrink-0
                transition-colors duration-150 hover:no-underline
                ${isActive(tab.path)
                  ? 'bg-zinc-100 !text-zinc-900'
                  : '!text-zinc-500 hover:!text-zinc-900 hover:bg-zinc-50'
                }
              `}
            >
              {tab.name}
            </Link>
          ))}
        </div>
      </nav>
    </header>
  );
};

// Download counts are Hugging Face totals (including community quantized builds);
// star counts are from GitHub. Keep in sync with the CV.
const highlightedWork = [
  {
    title: 'Fara-1.5: Scalable Learning Environments for Computer Use Agents',
    href: 'https://arxiv.org/abs/2606.20785',
    description: 'a family of open-weight computer-use models (4B, 9B, 27B)',
    stats: [
      { icon: 'download', label: '1M+ downloads', href: 'https://huggingface.co/collections/microsoft/fara15' },
    ],
  },
  {
    title: 'Fara-7B: An Efficient Agentic Model for Computer Use',
    href: 'https://arxiv.org/abs/2511.19663',
    description: 'a SOTA computer-use model for its size',
    stats: [
      { icon: 'download', label: '900k+ downloads', href: 'https://huggingface.co/microsoft/Fara-7B' },
      { icon: 'star', label: '6.2k stars', href: 'https://github.com/microsoft/fara' },
    ],
  },
  {
    title: 'Magentic-UI: A human-centered interface for interacting with AI agents',
    href: 'https://github.com/microsoft/magentic-ui',
    description: 'an open-source interface for interacting with AI agents',
    stats: [
      { icon: 'star', label: '10.1k stars', href: 'https://github.com/microsoft/magentic-ui' },
    ],
  },
  {
    title: 'Magentic-one: A generalist multi-agent system for solving complex tasks',
    href: 'https://arxiv.org/pdf/2411.04468?',
    description: 'multi-agent team based on frontier models SOTA (at release) for agentic AI benchmarks (GAIA)',
    stats: [
      { icon: 'star', label: '61k stars (AutoGen)', href: 'https://github.com/microsoft/autogen' },
    ],
  },
];

const socialLinkClass = "!text-zinc-500 hover:!text-zinc-900 p-2 md:p-1 rounded-lg hover:bg-zinc-100 transition-colors hover:no-underline";

const HomePage = () => (
  <div>
    <div className="flex flex-col md:flex-row items-start gap-6 md:gap-10">
      {/* Profile image and social links - shown first on mobile */}
      <div className="w-full md:w-48 md:flex-shrink-0 md:order-2 flex flex-col items-start gap-3 md:gap-4">
        <img
          src={profilePic}
          alt="Hussein Mozannar"
          className="w-20 h-20 flex-shrink-0 md:w-full md:h-auto rounded-full md:rounded-2xl object-cover ring-1 ring-zinc-200 shadow-sm"
        />
        <div className="flex flex-nowrap justify-start gap-0 md:gap-0.5 md:w-full -ml-2 md:-ml-1">
          <a href="https://scholar.google.com/citations?user=XCfZyIkAAAAJ&hl=en" target="_blank" rel="noopener noreferrer" className={socialLinkClass} aria-label="Google Scholar" title="Google Scholar">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5.242 13.769L0 9.5 12 0l12 9.5-5.242 4.269C17.548 11.249 14.978 9.5 12 9.5c-2.977 0-5.548 1.749-6.758 4.269zM12 10a7 7 0 1 0 0 14 7 7 0 0 0 0-14z" />
            </svg>
          </a>
          <a href="mailto:hssein.mzannar@gmail.com" className={socialLinkClass} aria-label="Email" title="Email">
            <Mail size={18} />
          </a>
          <a href="https://github.com/husseinmozannar" target="_blank" rel="noopener noreferrer" className={socialLinkClass} aria-label="GitHub" title="GitHub">
            <Github size={18} />
          </a>
          <a href={cvLink} download="mozannar_cv.pdf" target="_blank" rel="noopener noreferrer" className={socialLinkClass} aria-label="CV" title="CV">
            <FileText size={18} />
          </a>
          <a href="https://www.linkedin.com/in/husseinmozannar/" target="_blank" rel="noopener noreferrer" className={socialLinkClass} aria-label="LinkedIn" title="LinkedIn">
            <Linkedin size={18} />
          </a>
          <a href="https://x.com/HsseinMzannar" target="_blank" rel="noopener noreferrer" className={socialLinkClass} aria-label="X" title="X">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
        </div>
      </div>

      {/* Main content */}
      <div className="w-full md:flex-1 md:order-1 min-w-0">
        <h1 className="text-4xl md:text-[2.6rem] font-semibold tracking-tight mb-4">Hussein Mozannar</h1>
        {/* <p className="text-xl font-bold mb-4">
          Senior Researcher at Microsoft Research AI Frontiers
        </p> */}
        <div className="space-y-3.5 text-[0.97rem] leading-relaxed text-zinc-700">
          <p>
            I am a Member of Technical Staff at <a href="https://microsoft.ai/">Microsoft AI</a> on the post-training team, focusing on reinforcement learning for computer use. Previously, I was a Senior Researcher at <a href="https://www.microsoft.com/en-us/research/lab/ai-frontiers/">Microsoft Research AI Frontiers</a> building AI agents. I obtained my PhD in Statistics from MIT in 2024 and my undergraduate degree from the American University of Beirut in 2019.
          </p>

          <p>
            My goal is to build AI to assist people in their daily life and work. I focus in my research on improving models' ability to interact with people and enabling new applications through both post-training and interface design.
          </p>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Recent Work:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {highlightedWork.map((work) => (
                <li key={work.title} className="flex flex-col rounded-lg border border-zinc-200 px-3 py-2 hover:border-zinc-300 transition-colors">
                  <a href={work.href} className="text-[0.88rem] font-medium leading-snug">{work.title}</a>
                  <p className="mt-1 text-[0.82rem] leading-snug text-zinc-500">{work.description}</p>
                  <div className="mt-auto pt-1.5 flex flex-wrap gap-1.5">
                    {work.stats.map((stat) => (
                      <a
                        key={stat.label}
                        href={stat.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[0.72rem] font-medium leading-4 !text-zinc-600 hover:!text-zinc-900 hover:bg-zinc-200 hover:no-underline whitespace-nowrap"
                      >
                        {stat.icon === 'download' ? <Download size={11} /> : <Star size={11} />}
                        {stat.label}
                      </a>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <p>
            Read more about my past and current research in the <Link to="/research">Research</Link> section.
          </p>

          <p>You can reach me at <a href="mailto:hssein.mzannar@gmail.com">hssein.mzannar@gmail.com</a>, and for work-related queries at <a href="mailto:hmozannar@microsoft.com">hmozannar@microsoft.com</a>.</p>
        </div>
      </div>
    </div>
  </div>
);

const PublicationsPage = () => (
  <div>
    <h1 className="page-title">Publications</h1>
    <PublicationList publications={examplePublications} />
  </div>
);

const ProjectsPage = () => (
  <div>
    <h1 className="page-title">Projects</h1>
    {/* Projects content goes here */}
  </div>
);

const BlogPage = () => (
  <div>
    <Blog />
  </div>
);

const App = () => {
  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen">
        <Tabs />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:pt-9 md:pb-6">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/publications" element={<PublicationsPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/talks" element={<Talks />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<Blog />} />
        </Routes>
        </main>
      </div>
    </Router>
  );
};

export default App;